import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, ChildActivationEnd, Router } from '@angular/router';
import { UtilityService } from '@common/services';
import { TourOrchestratorService } from '@modules/onboarding/services';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

import { SBRouteData } from '../models';

@Injectable()
export class NavigationService implements OnDestroy {
    _sideNavVisible$ = new BehaviorSubject(true);
    _routeData$ = new BehaviorSubject({} as SBRouteData);
    _currentURL$ = new BehaviorSubject('');

    // Memoria para el estado del sidebar antes de colapsarlo temporalmente
    private savedSideNavState: boolean | null = null;
    private readonly _destroy$ = new Subject<void>();

    constructor(public route: ActivatedRoute, public router: Router, private utilityService: UtilityService, private tourOrchestrator: TourOrchestratorService) {
        // Escuchar cambios de ruta para actualizar routeData y currentURL. `takeUntil(this._destroy$)`: el
        // servicio es `@Injectable()` (provisto en NavigationModule, no `root`), así que puede destruirse/re-
        // crearse; sin esto la suscripción quedaría huérfana. Consistente con la de `tourState$` (mismo cleanup).
        this.router.events
            .pipe(
                takeUntil(this._destroy$),
                filter(event => event instanceof ChildActivationEnd),
            )
            .subscribe(event => {
                let snapshot = (event as ChildActivationEnd).snapshot;
                while (snapshot.firstChild !== null) {
                    snapshot = snapshot.firstChild;
                }
                this._routeData$.next(snapshot.data as SBRouteData);
                this._currentURL$.next(router.url);
            });

        // ── TOUR GUARD DEFINITIVO: Simplicidad y UX ──
        // Si hay un tour activo, mantenemos el sidebar expandido todo el tiempo
        // para dar espacio a los modales. Cuando el tour termine (!isActive),
        // aplicamos el colapso si el POS lo había solicitado.
        this.tourOrchestrator.tourState$
            .pipe(
                takeUntil(this._destroy$),
                distinctUntilChanged((a, b) => a.isActive === b.isActive),
            )
            .subscribe(state => {
                if (state.isActive) {
                    this.expandSideNav();
                } else if (this.savedSideNavState !== null) {
                    // `!state.isActive` es siempre verdadero en esta rama (ya pasó el `if (state.isActive)`).
                    this.collapseSideNav();
                }
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    // ---- Getters de observables públicos ------------------------------------

    sideNavVisible$(): Observable<boolean> {
        return this._sideNavVisible$;
    }

    routeData$(): Observable<SBRouteData> {
        return this._routeData$;
    }

    currentURL$(): Observable<string> {
        return this._currentURL$;
    }

    // ---- Control del sidebar ------------------------------------------------

    toggleSideNav(visibility?: boolean) {
        if (typeof visibility !== 'undefined') {
            this._sideNavVisible$.next(visibility);
        } else {
            this._sideNavVisible$.next(!this._sideNavVisible$.value);
        }
    }

    /**
     * Close sidebar after navigation clicks (mobile only)
     * On desktop, users can see content alongside the menu, so we don't close it.
     * On mobile, we close it so users can see the page they navigated to.
     */
    closeSideNavIfOpenInMobile() {
        if (this.utilityService.window.innerWidth >= 1200) {
            return; // Desktop - don't close on navigation
        }
        // Mobile: close the sidebar (uses inverted CSS logic)
        this.toggleSideNav(true);
    }

    /**
     * Collapse the sidenav (hide it) - works on both mobile and desktop
     * Note: CSS behavior is inverted between screen sizes:
     * - Desktop (≥1200px): toggleSideNav(false) = hidden
     * - Mobile (<1200px): toggleSideNav(true) = hidden
     */
    collapseSideNav() {
        if (this.tourOrchestrator.currentState.isActive) {
            return;
        }
        const isDesktop = this.utilityService.window.innerWidth >= 1200;
        this.toggleSideNav(isDesktop ? false : true);
    }

    /**
     * Expand the sidenav (show it) - works on both mobile and desktop
     * Note: CSS behavior is inverted between screen sizes:
     * - Desktop (≥1200px): toggleSideNav(true) = visible
     * - Mobile (<1200px): toggleSideNav(false) = visible
     */
    expandSideNav() {
        const isDesktop = this.utilityService.window.innerWidth >= 1200;
        this.toggleSideNav(isDesktop ? true : false);
    }

    /**
     * Colapsa el sidebar temporalmente, guardando su estado.
     * Si hay un tour activo, guarda la intención pero NO colapsa.
     */
    collapseTemporarily(): void {
        if (this.savedSideNavState === null) {
            this.savedSideNavState = this._sideNavVisible$.getValue();
        }

        if (this.tourOrchestrator.currentState.isActive) {
            return;
        }

        this.collapseSideNav();
    }

    // Restaura el menú lateral al valor exacto del BehaviorSubject que tenía antes de llamar a collapseTemporarily()
    restoreSideNavState(): void {
        if (this.savedSideNavState !== null) {
            this.toggleSideNav(this.savedSideNavState);
            this.savedSideNavState = null;
        }
    }
}
